import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { DateSelectionService } from "../services/date-selection.service";
import { MealService } from "../core/services/meal.service";
import { UserService } from "../core/services/user.service";
import { MealMenuAdminService } from "../core/services/meal-menu-admin.service";
import { filter, Subscription } from "rxjs";
import {
  generateDateOptions,
  getMealData as getMealDataCommon,
  DateOptionWithBadge,
  applyMealMenusToDateOptions,
} from "../shared/utils/commenFunctions";
import { pageLoadAnimationBottomToCenter } from "../shared/utils/common-animations";
import { getUserDetails } from "../shared/utils/storage-utils";
import { scrollToTop } from "../shared/utils/scroll-utils";

interface MealPreference {
  preference?: string;
  transport?: string;
  drinks?: string;
}

interface MealDataItem {
  mealDate?: string;
  date?: string;
  mealPreference?: MealPreference[];
  count?: number;
  deliveryStatus?: string;
  submitted?: boolean;
  locationName?: string;
}

interface MealDataResponse {
  results?: MealDataItem[];
  statusCode?: number;
  message?: string;
}

interface UserCountEntry {
  date?: string;
  counts?: {
    pending?: number;
    waitingList?: number;
    waitinglist?: number;
    waitList?: number;
    waitlist?: number;
    todayPending?: number;
  };
}

interface UserCountResponse {
  data?: UserCountEntry[];
  statusCode?: number;
  message?: string;
}

type DateCardState = "today" | "selected" | "default";

interface DateCardOption extends DateOptionWithBadge {
  state: DateCardState;
  submitted: boolean;
  waitingListCount?: number;
  mealType?: string;
  drink?: string;
  transport?: string;
  hotMenuText?: string;
  coldMenuText?: string;
}

@Component({
  selector: "scheduled-confirmed-component, ScheduledConfirmedComponent",
  standalone: true,
  templateUrl: './scheduled-confirmed.component.html',
  styleUrls: ['./scheduled-confirmed.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimationBottomToCenter]
})
export class ScheduledConfirmedComponent implements OnInit, OnDestroy {
  private routerSubscription?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dateSelectionService: DateSelectionService,
    private readonly mealService: MealService,
    private readonly userService: UserService,
    private readonly mealMenuAdminService: MealMenuAdminService,
  ) {}
  userDetails: { value?: string | number } = getUserDetails();
  pageLoadState = 'hidden';
  dateOptions: DateCardOption[] = [];
  mealData: MealDataResponse | null = null;
  userCount: UserCountResponse | null = null;
  allDaysSelected = false;
  todayDateValue = '';
  private mealMenus: Array<{
    day?: string | null;
    hotMeal?: string | null;
    coldMeal?: string | null;
    specialEvents?: string | null;
  }> = [];

  private readonly headerCopy = {
    default: {
      title: "Do you want to Prebook for the Coming Days?",
      subtitle: "Do you want to Prebook for the Coming Days?"
    },
    allSelected: {
      title: "Your Meals & Transportation Are Scheduled!",
      subtitle: "Here is a summary of your upcoming selections. Click Edit Plan on any day to modify meals or transit."
    }
  } as const;

  handleCardClick(index: number): void {
    this.openEditPlanForDate(index);
  }

  onEditPlanClick(index: number): void {
    this.openEditPlanForDate(index);
  }

  private openEditPlanForDate(index: number): void {
    // When clicking a date card / Edit Plan, navigate to meal preference for that date
    this.dateSelectionService.setCurrentEditingDate(index);
    
    // Store current route so meal-preferance returns here after save
    sessionStorage.setItem('previousRoute', '/scheduled-confirmed');
    
    this.router.navigate(['/meal-preferance'], { queryParams: { date: this.dateOptions[index].dateValue } });
  }

  /** Plan summary rows for Edit Plan table. */
  get planSummaryRows(): Array<{
    index: number;
    dateLabel: string;
    dayLabel: string;
    dateValue: string;
    isToday: boolean;
    mealScheduled: boolean;
    transportScheduled: boolean;
  }> {
    return this.dateOptions
      .map((option, index) => {
        const transportNorm = String(option.transport ?? '').trim().toLowerCase();
        const mealType = String(option.mealType ?? '').trim();
        const mealScheduled =
          !!mealType && mealType.toLowerCase() !== 'no meal';
        const transportScheduled =
          !!transportNorm &&
          transportNorm !== 'none' &&
          transportNorm !== 'no need' &&
          transportNorm !== 'not required' &&
          transportNorm !== 'notrequired';
        return {
          index,
          dateLabel: option.dateLabel,
          dayLabel: option.dayLabel,
          dateValue: option.dateValue ?? '',
          isToday: option.dateValue === this.todayDateValue,
          mealScheduled,
          transportScheduled,
        };
      })
      .filter((row) => !!row.dateValue);
  }

  handleCardKeyDown(event: KeyboardEvent, index: number): void {
    // Handle Enter or Space key to trigger card click (accessibility)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleCardClick(index);
    }
  }

  ngOnInit(): void {
    // Prevent scroll on page load
    scrollToTop();
    
    // Generate date options dynamically (today + 3 more days)
    this.dateOptions = this.buildDateCardOptions();
    this.todayDateValue = this.dateOptions[0]?.dateValue ?? '';
    this.loadMealMenus();
    
    // getMealData will call getUserCount after processing to preserve state
    this.getMealData();

    // Subscribe to router events to refresh state when navigating back to this component
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Only refresh if we're on the scheduled-confirmed route
        if (event.urlAfterRedirects.includes('/scheduled-confirmed')) {
          // Regenerate date options to ensure they're up to date
          this.dateOptions = this.buildDateCardOptions();
          this.todayDateValue = this.dateOptions[0]?.dateValue ?? '';
          this.loadMealMenus();
          // getMealData will call getUserCount after processing to preserve state
          this.getMealData();
          // Prevent scroll when navigating back to this route
          scrollToTop();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private syncSelectionStateWithApi(): void {
    const futureOptions = this.dateOptions.filter(option => option.dateValue !== this.todayDateValue);
    this.allDaysSelected = futureOptions.length > 0 && futureOptions.every(option => option.state === 'selected');

    this.dateOptions = this.dateOptions.map((option, idx) => {
      if (option.dateValue === this.todayDateValue && option.state !== 'selected') {
        return { ...option, state: 'today' };
      }
      return option;
    });
  }

  get headerTitle(): string {
    if (this.allDaysSelected) {
      const baseTitle = this.headerCopy.allSelected.title;
      if (this.hasWaitingList) {
        return `${baseTitle} On Waiting List for ${this.waitingListDates}.`;
      }
      return baseTitle;
    }
    return this.headerCopy.default.title;
  }

  get headerSubtitle(): string {
    return this.allDaysSelected ? this.headerCopy.allSelected.subtitle : this.headerCopy.default.subtitle;
  }

  private getMealDataCount(dateValue: string): number {
    if (!this.mealData) {
      return 0;
    }
    
    const results = this.mealData.results ?? (Array.isArray(this.mealData) ? this.mealData : []);
    const mealItem = results.find((item: MealDataItem) => 
      (item.mealDate ?? item.date) === dateValue
    );
    
    return mealItem?.count ?? 0;
  }

  get hasWaitingList(): boolean {
    return this.dateOptions.some(option => 
      option.state === 'selected' && 
      option.badgeCount !== 0 && 
      option.badgeCount !== undefined && 
      option.badgeCount > 0
    );
  }

  get waitingListDates(): string {
    const waitingDates = this.dateOptions
      .filter(option => 
        option.state === 'selected'  && option?.badgeCount !== 0 && option?.badgeCount !== undefined && option?.badgeCount > 0
      )
      .map(option => option.dateLabel);
    
    if (waitingDates.length === 0) {
      return '';
    } else {
      return waitingDates.join(', ');
    }
  }

  onBackClick(): void {
   
    this.router.navigate(['/success-meal-transport']);
  }

  onNextClick(): void {
    // Navigate to activity schedule screen
    this.router.navigate(['/activity-screen']);
  }

  private getPreviousUrl(): string | null {
    const previousTree = this.router.lastSuccessfulNavigation?.previousNavigation?.finalUrl;
    return previousTree ? previousTree.toString() : null;
  }
  ngAfterViewInit(): void {
    // Prevent scroll on page load
    scrollToTop();
    
    // Trigger animation immediately after view init
    // Use a small delay to ensure Angular has processed the initial state
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
        // Ensure scroll position remains at top after animation
       
      }, 50);
    });
  }

  private buildDateCardOptions(): DateCardOption[] {
    return generateDateOptions().map((option, index) => ({
      ...option,
      state: index === 0 ? 'today' : 'default',
      badgeCount: 0,
      submitted: false,
      waitingListCount: 0,
      mealType: '',
      drink: '',
      transport: '',
      hotMenuText: '',
      coldMenuText: '',
    }));
  }

  private mealDietPreference: string | null = null;

  private loadMealMenus(): void {
    const userId = Number(this.userDetails?.value);
    this.mealMenuAdminService
      .listMenus(Number.isFinite(userId) && userId > 0 ? userId : null)
      .subscribe({
        next: (response) => {
          this.mealMenus = response?.menus ?? [];
          this.mealDietPreference = response?.mealDietPreference ?? null;
          this.applyMenusToCards();
        },
        error: () => {
          this.mealMenus = [];
        },
      });
  }

  private applyMenusToCards(): void {
    if (!this.mealMenus.length) {
      return;
    }
    this.dateOptions = applyMealMenusToDateOptions(
      this.dateOptions,
      this.mealMenus,
      this.mealDietPreference,
    );
  }

  getMealData(): void {
    getMealDataCommon(
      this.mealService,
      String(this.userDetails.value ?? ''),
      this.dateOptions,
      (response: MealDataResponse) => {
        this.mealData = response;
        this.updateDateOptionsFromMealData();
        // Call getUserCount after meal data is processed to preserve state
      },
      () => {
        this.mealData = null;
      }
    );
  }

  private updateDateOptionsFromMealData(): void {
    if (!this.mealData) {
      return;
    }
    
    // Extract dates from mealData response
    // API response structure: { statusCode: 200, message: "...", results: [{ mealDate: "2025-11-24", ... }] }
    // Note: API doesn't send previous dates, so we can process all dates from the response
    const mealDates = new Set<string>();
    const deliveredDates = new Set<string>();
    const mealCountMap = new Map<string, number>();
    const locationNameMap = new Map<string, string>();
    const mealChoiceMap = new Map<string, { mealType: string; drink: string; transport: string }>();

    // Check if mealData has results array
    if (this.mealData.results && Array.isArray(this.mealData.results)) {
      this.mealData.results.forEach((item: MealDataItem) => {
        if (item.mealDate) {
          if (item.mealPreference && Array.isArray(item.mealPreference) && item.mealPreference.length > 0) {
            mealDates.add(item.mealDate);
            mealCountMap.set(item.mealDate, item.count ?? 0);
            mealChoiceMap.set(item.mealDate, this.parseMealChoice(item.mealPreference));
          }
          if (item.locationName) {
            locationNameMap.set(item.mealDate, item.locationName);
          }
          if (item.deliveryStatus && item.deliveryStatus.toLowerCase() === 'delivered') {
            deliveredDates.add(item.mealDate);
          }
          if (item.submitted) {
            this.dateOptions.forEach(option => {
              if (option.dateValue === item.mealDate) {
                option.submitted = true;
              }
            });
          }
        }
      });
    } else if (Array.isArray(this.mealData)) {
      (this.mealData as unknown as MealDataItem[]).forEach((item: MealDataItem) => {
        const dateValue = item.mealDate ?? item.date;
        if (dateValue) {
          if (item.mealPreference && Array.isArray(item.mealPreference) && item.mealPreference.length > 0) {
            mealDates.add(dateValue);
            mealCountMap.set(dateValue, item.count ?? 0);
            mealChoiceMap.set(dateValue, this.parseMealChoice(item.mealPreference));
          }
          if (item.locationName) {
            locationNameMap.set(dateValue, item.locationName);
          }
          if (item.deliveryStatus && item.deliveryStatus.toLowerCase() === 'delivered') {
            deliveredDates.add(dateValue);
          }
          if (item.submitted) {
            this.dateOptions.forEach(option => {
              if (option.dateValue === dateValue) {
                option.submitted = true;
              }
            });
          }
        }
      });
    }

    // Update date options: filter out delivered dates, mark as selected if date exists in mealData
    this.dateOptions = this.dateOptions
      .filter(option => {
        if (!option.dateValue) return false;
        if (deliveredDates.has(option.dateValue)) return false;
        return true;
      })
      .map((option) => {
        const hasMealInfo = option.dateValue && mealDates.has(option.dateValue);
        const isToday = option.dateValue === this.todayDateValue;
        const newState = hasMealInfo ? (isToday ? 'selected' : 'selected') : (isToday ? 'today' : 'default');
        const badgeCount = hasMealInfo && mealCountMap.has(option.dateValue)
          ? mealCountMap.get(option.dateValue)!
          : option.badgeCount ?? 0;
        const locationName = option.dateValue ? (locationNameMap.get(option.dateValue) ?? '') : '';
        const choice = option.dateValue ? mealChoiceMap.get(option.dateValue) : undefined;

        return {
          ...option,
          state: newState,
          badgeCount: badgeCount,
          locationName: locationName,
          mealType: choice?.mealType ?? '',
          drink: choice?.drink ?? '',
          transport: choice?.transport ?? '',
        };
      });
    
    // Keep UI state in sync with API-provided selections
    this.syncSelectionStateWithApi();
    this.applyMenusToCards();
  }

  hasChoiceHistory(option: DateCardOption): boolean {
    return !!(option.mealType || option.drink || option.transport);
  }

  private parseMealChoice(preferences: MealPreference[]): {
    mealType: string;
    drink: string;
    transport: string;
  } {
    let mealType = '';
    let drink = '';
    let transport = '';

    for (const pref of preferences) {
      if (pref.preference) {
        mealType = pref.preference;
      }
      if (pref.drinks) {
        drink = pref.drinks;
      }
      if (pref.transport) {
        transport = pref.transport;
      }
    }

    return { mealType, drink, transport };
  }

}
